import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { OutgoingShipmentForm } from '../components/OutgoingShipmentForm';
import {
  useGetOutgoingShipmentFormQuery,
  useUpdateOutgoingShipmentMutation,
} from '../api/outgoingShipmentsApi';
import { useOutgoingShipmentFormOptions } from '../hooks';

// Outbounds ▸ Outgoing shipments ▸ Edit. Same form as Add.
export const EditOutgoingShipmentPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, shipmentId } = useParams();

  const {
    data,
    isLoading: isLoadingShipment,
    isError,
  } = useGetOutgoingShipmentFormQuery({ module, id: shipmentId });
  const { options, isLoading } = useOutgoingShipmentFormOptions();
  const [updateOutgoingShipment, { isLoading: isSubmitting }] = useUpdateOutgoingShipmentMutation();

  const goToShipments = useCallback(() => {
    navigate(ROUTES.outgoingShipments(module));
  }, [navigate, module]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        await updateOutgoingShipment({ module, id: shipmentId, values }).unwrap();
        goToShipments();
      } catch {
        // notifyApiErrors shows the server's message.
      }
    },
    [updateOutgoingShipment, module, shipmentId, goToShipments],
  );

  // Wait for both: <Form> seeds initialValues once, on mount.
  if (isLoadingShipment || isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !data) {
    return (
      <EmptyState
        title={t('OUTGOING_SHIPMENTS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToShipments}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <OutgoingShipmentForm
      title={t('OUTGOING_SHIPMENTS_EDIT_TITLE')}
      submitLabel={t('COMMON_SAVE')}
      initialValues={data.values}
      onSubmit={handleSubmit}
      onClose={goToShipments}
      isSubmitting={isSubmitting}
      options={options}
      mode="edit"
      lockedExceptQuantity={data.lockedExceptQuantity}
      assignedByUnit={data.assignedByUnit}
    />
  );
};
